import React from "react";
import { useNavigate } from "react-router-dom";
import { FileText } from "lucide-react";
import { Panel, money, InlineError } from "./singleUtils";

const Chip = ({ label, value, tone = "text-foreground", testid, onClick }) => (
  <button onClick={onClick} className="rounded-sm border border-border/60 bg-muted/20 px-2 py-1.5 text-left hover:border-amber-500/40 transition-colors" data-testid={testid}>
    <p className={`text-base font-extrabold font-mono leading-tight ${tone}`}>{value}</p>
    <p className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground mt-0.5">{label}</p>
  </button>
);

const InvoicePipeline = ({ outstanding, documents, financialAccess, onRetry }) => {
  const navigate = useNavigate();
  if (!financialAccess) return null;
  const o = outstanding || {};
  const d = documents || {};
  if (o.error && d.error) return <Panel title="Invoices" icon={FileText} testid="invoice-pipeline"><InlineError label="invoice data" onRetry={onRetry} /></Panel>;
  const unpaid = Math.max((o.count || 0) - (o.partial_count || 0), 0);
  const go = (qs) => navigate(`/dashboard/invoices${qs || ""}`);
  return (
    <Panel title="Invoices" icon={FileText} testid="invoice-pipeline"
      right={<button onClick={() => go()} className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:underline">Open →</button>}>
      <div className="grid grid-cols-3 gap-1.5">
        <Chip label="Unpaid" value={unpaid} tone={unpaid > 0 ? "text-rose-600 dark:text-rose-500" : "text-foreground"} onClick={() => go("?status=open")} testid="inv-unpaid" />
        <Chip label="Partial" value={o.partial_count ?? 0} tone="text-orange-500" onClick={() => go("?status=partially_paid")} testid="inv-partial" />
        <Chip label="Overdue" value={o.overdue_count ?? 0} tone={(o.overdue_count || 0) > 0 ? "text-rose-600 dark:text-rose-500" : "text-foreground"} onClick={() => go("?status=overdue")} testid="inv-overdue" />
        <Chip label="Paid (period)" value={d.paid_in_period ?? 0} tone="text-emerald-600 dark:text-emerald-400" onClick={() => go("?status=paid")} testid="inv-paid" />
        <Chip label="Drafts" value={d.drafts ?? 0} onClick={() => go("?status=draft")} testid="inv-drafts" />
        <Chip label="Est. Pending" value={d.estimates?.pending ?? 0} onClick={() => go("?type=estimate")} testid="inv-est-pending" />
      </div>
      {(o.total || 0) > 0 && (
        <div className="mt-2 flex items-center justify-between rounded-sm border border-rose-500/25 bg-rose-500/[0.05] px-2.5 py-1.5" data-testid="inv-outstanding-total">
          <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">Outstanding</span>
          <span className="text-sm font-extrabold font-mono text-rose-600 dark:text-rose-500">{money(o.total)}</span>
        </div>
      )}
      {(o.total || 0) === 0 && !o.error && (
        <p className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium" data-testid="inv-all-clear">No unpaid invoices — you're all caught up</p>
      )}
    </Panel>
  );
};

export default InvoicePipeline;
